import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OnboardingHeader from '../../components/hospitalOnboarding/OnboardingHeader';
import { useHospitalOnboarding } from '../../contexts/HospitalOnboardingContext';
import {
  Building2,
  Truck,
  User,
  Mail,
  Phone,
  Lock,
  MapPin,
  FileText,
  Eye,
  EyeOff,
  ArrowRight,
  PhoneCall,
  CheckCircle2,
  ChevronDown,
  AlertCircle,
  Building,
  ShieldCheck,
  Hash,
  Loader2,
  LogOut
} from 'lucide-react';

import {
  hospitalOnboardingService,
  initialOnboardingData,
  initialFleetOnboardingData
} from '../../services/hospitalOnboardingService';

export const HospitalPartnerRegistrationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { formData, updateFields, setCurrentStep, setFormData, setApplicationId, startOver, showToast } = useHospitalOnboarding();

  // Partner selection: 'hospital' | 'fleet'
  const initialType = searchParams.get('type') === 'fleet' ? 'fleet' : 'hospital';
  const [partnerType, setPartnerType] = useState(initialType);

  // Password visibility states
  const [showHospitalPassword, setShowHospitalPassword] = useState(false);
  const [showFleetPassword, setShowFleetPassword] = useState(false);
  const [showFleetConfirmPassword, setShowFleetConfirmPassword] = useState(false);

  // Submitting state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Errors state
  const [errors, setErrors] = useState({});

  // Fleet operators list for hospital dropdown (dynamic backend lookup)
  const [fleetOperators, setFleetOperators] = useState([]);
  const [loadingOperators, setLoadingOperators] = useState(true);

  useEffect(() => {
    let isMounted = true;
    hospitalOnboardingService
      .getFleetOperatorsLookup()
      .then((ops) => {
        if (isMounted && Array.isArray(ops) && ops.length > 0) {
          setFleetOperators(ops);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (isMounted) setLoadingOperators(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // When visiting registration page, clear any legacy fake simulation application ID
  useEffect(() => {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem('risa_current_application_id');
      if (stored && stored.startsWith('RISA-APP-')) {
        localStorage.removeItem('risa_current_application_id');
      }
    }
  }, []);

  // When visiting registration page after a prior submission or stale simulation, reset lingering state
  useEffect(() => {
    if (
      formData?.status === 'SUBMITTED' ||
      (formData?.application_id && String(formData.application_id).startsWith('RISA-APP-'))
    ) {
      if (startOver) {
        startOver(partnerType);
      }
    }
  }, [formData?.status, formData?.application_id, partnerType, startOver]);

  // Active user session detection (Review Step 1 Fix)
  const [activeSession, setActiveSession] = useState(null);

  useEffect(() => {
    try {
      if (typeof localStorage !== 'undefined') {
        const token = localStorage.getItem('token') || localStorage.getItem('aps_jwt_token');
        const userDataStr = localStorage.getItem('aps_user_data');
        if (token && userDataStr) {
          const parsed = JSON.parse(userDataStr);
          setActiveSession({
            name: parsed.name || parsed.admin_name || 'Active User',
            email: parsed.email || parsed.admin_email || '',
            role: parsed.role || 'User'
          });
        }
      }
    } catch (e) {
      console.warn('Could not read existing user session:', e);
    }
  }, []);

  const handleSignOutCurrentSession = () => {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('token');
        localStorage.removeItem('aps_jwt_token');
        localStorage.removeItem('aps_user_data');
        localStorage.removeItem('userRole');
        localStorage.removeItem('user');
        localStorage.removeItem('risa_current_application_id');
        localStorage.removeItem('risa_partner_type');
      }
      hospitalOnboardingService.clearDraft();
      setActiveSession(null);
      if (showToast) {
        showToast('Signed out of previous session. You can now register a new partner.', 'info');
      }
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  // 1. Hospital Form State (Blank by default, displaying only placeholders)
  const [hospitalForm, setHospitalForm] = useState({
    name: '',
    licenseNumber: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    operator_id: '',
    admin_name: '',
    admin_email: '',
    admin_phone_country_code: '+91',
    admin_phone: '',
    admin_temporary_password: ''
  });

  // 2. Fleet Operator Form State (Blank by default, displaying only placeholders)
  const [fleetForm, setFleetForm] = useState({
    operator_name: '',
    contact_info: '',
    admin_name: '',
    admin_email: '',
    admin_phone_country_code: '+91',
    admin_phone: '',
    admin_temporary_password: '',
    confirmPassword: '',
    business_license: '',
    emergency_contact_country_code: '+91',
    emergency_contact: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    service_area: ''
  });

  const handleHospitalChange = (field, value) => {
    setHospitalForm((prev) => ({ ...prev, [field]: value }));
    const errorKey =
      field === 'name' ? 'hospital_name' :
      field === 'address' ? 'hospital_address' :
      field === 'city' ? 'hospital_city' :
      field === 'state' ? 'hospital_state' :
      field === 'pincode' ? 'hospital_pincode' : field;
    if (errors[errorKey] || errors[field]) {
      setErrors((prev) => ({ ...prev, [errorKey]: '', [field]: '', submit: '' }));
    }
  };

  const handleFleetChange = (field, value) => {
    setFleetForm((prev) => ({ ...prev, [field]: value }));
    const errorKey =
      field === 'admin_name' ? 'fleet_admin_name' :
      field === 'admin_email' ? 'fleet_admin_email' :
      field === 'admin_phone' ? 'fleet_admin_phone' :
      field === 'admin_temporary_password' ? 'fleet_admin_temporary_password' :
      field === 'confirmPassword' ? 'fleet_confirm_password' : field;
    if (errors[errorKey] || errors[field]) {
      setErrors((prev) => ({ ...prev, [errorKey]: '', [field]: '', submit: '' }));
    }
  };

  // Generate temporary password helper using crypto.getRandomValues
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    const array = new Uint32Array(10);
    window.crypto.getRandomValues(array);
    let pwd = '';
    for (let i = 0; i < 10; i++) {
      pwd += chars.charAt(array[i] % chars.length);
    }
    return pwd;
  };

  const handleGenerateHospitalPassword = () => {
    const newPwd = generatePassword();
    handleHospitalChange('admin_temporary_password', newPwd);
  };

  const handleGenerateFleetPassword = () => {
    const newPwd = generatePassword();
    setFleetForm((prev) => ({
      ...prev,
      admin_temporary_password: newPwd,
      confirmPassword: newPwd
    }));
    if (errors.fleet_admin_temporary_password || errors.fleet_confirm_password || errors.admin_temporary_password || errors.confirmPassword) {
      setErrors((prev) => ({
        ...prev,
        admin_temporary_password: '',
        confirmPassword: '',
        fleet_admin_temporary_password: '',
        fleet_confirm_password: '',
        submit: ''
      }));
    }
  };

  // Validate form fields for hospital or fleet
  const validateForm = () => {
    const newErrors = {};

    if (partnerType === 'hospital') {
      if (!hospitalForm.name.trim()) newErrors.hospital_name = 'Hospital name is required';
      if (!hospitalForm.licenseNumber.trim()) newErrors.licenseNumber = 'License number is required';
      if (!hospitalForm.address.trim()) newErrors.hospital_address = 'Address is required';
      if (!hospitalForm.city.trim()) newErrors.hospital_city = 'City is required';
      if (!hospitalForm.state.trim()) newErrors.hospital_state = 'State is required';
      if (!hospitalForm.pincode.trim()) {
        newErrors.hospital_pincode = 'Pincode is required';
      } else if (!/^\d{6}$/.test(hospitalForm.pincode.trim())) {
        newErrors.hospital_pincode = 'Pincode must be 6 digits';
      }
      if (!hospitalForm.admin_name.trim()) newErrors.admin_name = 'Admin name is required';
      if (!hospitalForm.admin_email.trim()) {
        newErrors.admin_email = 'Admin email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(hospitalForm.admin_email.trim())) {
        newErrors.admin_email = 'Invalid email address';
      }
      if (!hospitalForm.admin_phone.trim()) {
        newErrors.admin_phone = 'Admin phone is required';
      } else if (!/^\d{10}$/.test(hospitalForm.admin_phone.trim())) {
        newErrors.admin_phone = 'Valid 10-digit phone number is required';
      }
      if (!hospitalForm.admin_temporary_password) {
        newErrors.admin_temporary_password = 'Temporary password is required';
      }
    } else {
      if (!fleetForm.operator_name.trim()) newErrors.operator_name = 'Fleet operator name is required';
      if (!fleetForm.contact_info.trim()) newErrors.contact_info = 'Contact information is required';
      if (!fleetForm.admin_name.trim()) newErrors.fleet_admin_name = 'Admin name is required';
      if (!fleetForm.admin_email.trim()) {
        newErrors.fleet_admin_email = 'Admin email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fleetForm.admin_email.trim())) {
        newErrors.fleet_admin_email = 'Invalid email address';
      }
      if (!fleetForm.admin_phone.trim()) {
        newErrors.fleet_admin_phone = 'Admin phone is required';
      } else if (!/^\d{10}$/.test(fleetForm.admin_phone.trim())) {
        newErrors.fleet_admin_phone = 'Valid 10-digit phone number is required';
      }
      if (!fleetForm.admin_temporary_password) {
        newErrors.fleet_admin_temporary_password = 'Temporary password is required';
      }
      if (!fleetForm.confirmPassword) {
        newErrors.fleet_confirm_password = 'Confirm password is required';
      } else if (fleetForm.admin_temporary_password !== fleetForm.confirmPassword) {
        newErrors.fleet_confirm_password = 'Passwords do not match';
      }
    }

    return newErrors;
  };

  // Click handler for "Need assisted voice intake? Start voice"
  const handleVoiceIntakeClick = (e) => {
    if (e) e.preventDefault();
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors({
        ...validationErrors,
        submit: 'Please complete the partner registration form first before starting voice intake.'
      });
      if (showToast) {
        showToast('Please complete the partner registration form first before starting voice intake.', 'error');
      }
      setTimeout(() => {
        const firstErrorEl = document.querySelector('[data-error="true"]') || document.querySelector('form');
        if (firstErrorEl) {
          firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
      return;
    }

    // Form is completely filled! Register and directly proceed to voice intake
    handleSubmit(e, { directToVoice: true });
  };

  // Form Submission - Calls backend register API and sets up onboarding session
  const handleSubmit = async (e, options = {}) => {
    if (e) e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors({
        ...validationErrors,
        submit: options?.directToVoice
          ? 'Please complete the partner registration form first before starting voice intake.'
          : 'Please complete all required fields before continuing.'
      });
      if (showToast) {
        showToast(
          options?.directToVoice
            ? 'Please complete the partner registration form first before starting voice intake.'
            : 'Please complete all required fields before continuing.',
          'error'
        );
      }
      setTimeout(() => {
        const firstErrorEl = document.querySelector('[data-error="true"]') || document.querySelector('form');
        if (firstErrorEl) {
          firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      if (partnerType === 'hospital') {
        const apiPayload = {
          partnerType: 'hospital',
          name: hospitalForm.name.trim(),
          licenseNumber: hospitalForm.licenseNumber.trim(),
          address: hospitalForm.address.trim(),
          city: hospitalForm.city.trim(),
          state: hospitalForm.state.trim(),
          pincode: hospitalForm.pincode.trim(),
          operator_id: hospitalForm.operator_id || null,
          admin_name: hospitalForm.admin_name.trim(),
          admin_email: hospitalForm.admin_email.trim(),
          admin_phone: hospitalForm.admin_phone.trim(),
          admin_phone_country_code: hospitalForm.admin_phone_country_code || '+91',
          admin_password: hospitalForm.admin_temporary_password
        };

        const result = await hospitalOnboardingService.registerPartner(apiPayload);
        const appId = result.data?.applicationId || result.applicationId;
        const authToken = result.data?.token || result.token;
        const authUser = result.data?.user || result.user;

        // Only replace the session when the backend actually issued new credentials.
        // When this request was made by an already-authenticated user (e.g. an
        // onboarding staff member), the backend intentionally returns no token and
        // reuses the existing session - wiping it here would log that user out.
        if (authToken) {
          if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('token');
            localStorage.removeItem('aps_jwt_token');
            localStorage.removeItem('aps_user_data');
            localStorage.removeItem('userRole');
            localStorage.removeItem('user');
            localStorage.setItem('token', authToken);
            localStorage.setItem('aps_jwt_token', authToken);
          }
          if (authUser) {
            localStorage.setItem('aps_user_data', JSON.stringify(authUser));
          }
        }

        // Clean up any old working draft / application ID before creating new
        hospitalOnboardingService.clearDraft();
        localStorage.removeItem('risa_current_application_id');

        if (appId) {
          localStorage.setItem('risa_current_application_id', appId);
          if (setApplicationId) setApplicationId(appId);
        }

        const freshHospitalPayload = {
          ...JSON.parse(JSON.stringify(initialOnboardingData)),
          partnerType: 'hospital',
          hospital_name: hospitalForm.name.trim(),
          license_number: hospitalForm.licenseNumber.trim(),
          full_address: hospitalForm.address.trim(),
          address: hospitalForm.address.trim(),
          city: hospitalForm.city.trim(),
          state: hospitalForm.state.trim(),
          pincode: hospitalForm.pincode.trim(),
          operator_id: hospitalForm.operator_id,
          admin_name: hospitalForm.admin_name.trim(),
          email: hospitalForm.admin_email.trim(),
          admin_email: hospitalForm.admin_email.trim(),
          mobile: hospitalForm.admin_phone.trim(),
          admin_phone: hospitalForm.admin_phone.trim(),
          application_id: appId,
          status: 'IN_PROGRESS',
          current_step: 1
        };

        if (setFormData) {
          setFormData(freshHospitalPayload);
        } else {
          updateFields(freshHospitalPayload);
        }
        hospitalOnboardingService.saveDraft(freshHospitalPayload, authUser?.user_id, appId);
        localStorage.setItem('risa_partner_type', 'hospital');

        setCurrentStep(1);
        if (options?.directToVoice) {
          navigate(`/hospital/onboarding/voice?applicationId=${appId}&type=hospital`);
        } else {
          navigate(`/hospital/onboarding?applicationId=${appId}&type=hospital`);
        }
      } else {
        const apiPayload = {
          partnerType: 'fleet',
          operator_name: fleetForm.operator_name.trim(),
          business_license: fleetForm.business_license.trim(),
          contact_info: fleetForm.contact_info.trim(),
          emergency_contact: fleetForm.emergency_contact.trim(),
          service_area: fleetForm.service_area.trim(),
          address: fleetForm.address.trim(),
          city: fleetForm.city.trim(),
          state: fleetForm.state.trim(),
          pincode: fleetForm.pincode.trim(),
          admin_name: fleetForm.admin_name.trim(),
          admin_email: fleetForm.admin_email.trim(),
          admin_phone: fleetForm.admin_phone.trim(),
          admin_phone_country_code: fleetForm.admin_phone_country_code || '+91',
          admin_password: fleetForm.admin_temporary_password
        };

        const result = await hospitalOnboardingService.registerPartner(apiPayload);
        const appId = result.data?.applicationId || result.applicationId;
        const authToken = result.data?.token || result.token;
        const authUser = result.data?.user || result.user;

        // Only replace the session when the backend actually issued new credentials.
        // When this request was made by an already-authenticated user (e.g. an
        // onboarding staff member), the backend intentionally returns no token and
        // reuses the existing session - wiping it here would log that user out.
        if (authToken) {
          if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('token');
            localStorage.removeItem('aps_jwt_token');
            localStorage.removeItem('aps_user_data');
            localStorage.removeItem('userRole');
            localStorage.removeItem('user');
            localStorage.setItem('token', authToken);
            localStorage.setItem('aps_jwt_token', authToken);
          }
          if (authUser) {
            localStorage.setItem('aps_user_data', JSON.stringify(authUser));
          }
        }

        // Clean up any old working draft / application ID before creating new
        hospitalOnboardingService.clearDraft();
        localStorage.removeItem('risa_current_application_id');

        if (appId) {
          localStorage.setItem('risa_current_application_id', appId);
          if (setApplicationId) setApplicationId(appId);
        }

        const freshFleetPayload = {
          ...JSON.parse(JSON.stringify(initialFleetOnboardingData)),
          partnerType: 'fleet',
          operator_name: fleetForm.operator_name.trim(),
          contact_info: fleetForm.contact_info.trim(),
          admin_name: fleetForm.admin_name.trim(),
          email: fleetForm.admin_email.trim(),
          admin_email: fleetForm.admin_email.trim(),
          mobile: fleetForm.admin_phone.trim(),
          admin_phone: fleetForm.admin_phone.trim(),
          business_license: fleetForm.business_license.trim(),
          emergency_contact: fleetForm.emergency_contact.trim(),
          full_address: fleetForm.address.trim(),
          address: fleetForm.address.trim(),
          city: fleetForm.city.trim(),
          state: fleetForm.state.trim(),
          pincode: fleetForm.pincode.trim(),
          service_area: fleetForm.service_area.trim(),
          application_id: appId,
          status: 'IN_PROGRESS',
          current_step: 1
        };

        if (setFormData) {
          setFormData(freshFleetPayload);
        } else {
          updateFields(freshFleetPayload);
        }
        hospitalOnboardingService.saveDraft(freshFleetPayload, authUser?.user_id, appId);
        localStorage.setItem('risa_partner_type', 'fleet');

        setCurrentStep(1);
        if (options?.directToVoice) {
          navigate(`/hospital/onboarding/voice?applicationId=${appId}&type=fleet`);
        } else {
          navigate(`/hospital/onboarding?applicationId=${appId}&type=fleet`);
        }
      }
    } catch (err) {
      console.error('Registration failed:', err);
      setErrors((prev) => ({
        ...prev,
        submit: err.message || 'Registration failed. Please check your credentials and try again.'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col font-sans relative overflow-x-hidden"
      style={{
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)'
      }}
    >
      {/* Top Header */}
      <OnboardingHeader />

      {/* Main Split Container */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 flex items-center justify-center">
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/60 border border-slate-200/80 overflow-hidden w-full grid grid-cols-1 lg:grid-cols-12 min-h-[700px]">
          
          {/* Left Column (Clean Brand Blue with Airy Spacing & Refined Typography) */}
          <div className="lg:col-span-4 bg-gradient-to-br from-blue-600 to-blue-800 text-white p-7 sm:p-9 flex flex-col justify-between relative overflow-hidden">
            {/* Ambient subtle light circles */}
            <div className="absolute top-0 right-0 -mr-16 -mt-16 w-72 h-72 bg-white/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl pointer-events-none" />

            {/* Top section */}
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-semibold tracking-wider uppercase bg-white/15 text-white border border-white/20 mb-6 select-none backdrop-blur-xs shadow-2xs">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span>RISA PARTNER NETWORK</span>
              </div>

              <h1 className="text-2xl sm:text-3xl font-semibold text-white leading-tight tracking-tight mb-3">
                One Number.<br />
                One Network.<br />
                <span className="text-sky-200 font-bold">One Partner.</span>
              </h1>

              <p className="text-xs sm:text-sm text-blue-100/90 leading-relaxed max-w-sm mb-8 font-normal">
                Join India's premier intelligent emergency and hospital transfer network.
              </p>

              {/* Connected 3 Step Indicators (Dynamically adapts to Hospital vs Fleet) */}
              <div className="space-y-0 relative">
                {/* Step 1 */}
                <div className="flex items-start gap-3 relative">
                  <div className="w-7 h-7 rounded-full bg-white text-blue-600 flex items-center justify-center text-xs font-semibold shrink-0 ring-4 ring-white/20 shadow-xs z-10">
                    1
                  </div>
                  <div>
                    <h4 className="text-xs sm:text-sm font-medium text-white leading-tight">
                      {partnerType === 'hospital' ? 'Register Hospital Profile' : 'Register Fleet Profile'}
                    </h4>
                    <p className="text-[11px] text-blue-100/80 mt-0.5">
                      {partnerType === 'hospital'
                        ? 'Basic hospital identification & location'
                        : 'Fleet operator details & credentials'}
                    </p>
                  </div>
                </div>

                {/* Connector Line */}
                <div className="w-0.5 h-5 bg-white/25 ml-3.5 my-1" />

                {/* Step 2 */}
                <div className="flex items-start gap-3 relative">
                  <div className="w-7 h-7 rounded-full bg-white/10 text-white/90 border border-white/20 flex items-center justify-center text-xs font-medium shrink-0 z-10 backdrop-blur-xs">
                    2
                  </div>
                  <div>
                    <h4 className="text-xs sm:text-sm font-medium text-white/90 leading-tight">
                      {partnerType === 'hospital' ? 'Facilities & Fleet Demand' : 'Fleet Capacity & Service Area'}
                    </h4>
                    <p className="text-[11px] text-blue-200/80 mt-0.5">
                      {partnerType === 'hospital'
                        ? 'Trauma, ICU beds & transport volume'
                        : 'Ambulance count, drivers & coverage'}
                    </p>
                  </div>
                </div>

                {/* Connector Line */}
                <div className="w-0.5 h-5 bg-white/25 ml-3.5 my-1" />

                {/* Step 3 */}
                <div className="flex items-start gap-3 relative">
                  <div className="w-7 h-7 rounded-full bg-white/10 text-white/90 border border-white/20 flex items-center justify-center text-xs font-medium shrink-0 z-10 backdrop-blur-xs">
                    3
                  </div>
                  <div>
                    <h4 className="text-xs sm:text-sm font-medium text-white/90 leading-tight">
                      {partnerType === 'hospital' ? 'Clinical Review & Live Activation' : 'Operational Review & Activation'}
                    </h4>
                    <p className="text-[11px] text-blue-200/80 mt-0.5">
                      Direct integration with RISA Command Desk
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Helpline Box */}
            <div className="pt-6 mt-8 border-t border-white/15 relative z-10">
              <div className="bg-white/10 hover:bg-white/15 border border-white/15 rounded-xl p-3.5 flex items-center gap-3 backdrop-blur-xs transition-all shadow-2xs group">
                <div className="w-9 h-9 rounded-lg bg-white/20 text-white flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                  <PhoneCall className="w-4 h-4 text-white" />
                </div>
                <div>
                  <span className="text-[11px] text-blue-100/90 block leading-tight">
                    Need a hand with onboarding?
                  </span>
                  <a
                    href="tel:+919904104104"
                    className="font-semibold text-sm text-white hover:text-sky-200 transition-colors leading-tight mt-0.5 block"
                  >
                    +91 9904 104 104
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right White Column (Clean, Neat & Polished Form with full fields from Image 1 & 2) */}
          <div className="lg:col-span-8 p-6 sm:p-8 lg:p-10 flex flex-col justify-between max-h-[85vh] overflow-y-auto">
            <div>
              {/* Header Title */}
              <div className="mb-6">
                <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider block mb-1">
                  Partner Registration
                </span>
                <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight">
                  Become a RISA partner
                </h2>
                <p className="text-xs sm:text-sm text-slate-500 mt-1 font-normal">
                  {partnerType === 'hospital'
                    ? 'Start with a few details about your hospital to begin onboarding.'
                    : 'Start with a few details about your fleet operator to begin onboarding.'}
                </p>
              </div>

              {/* Active Session Warning Banner (Review Step 1 Fix) */}
              {activeSession && (
                <div className="mb-6 p-4 rounded-2xl bg-amber-50/90 border border-amber-300 text-slate-800 shadow-2xs">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <h4 className="text-xs sm:text-sm font-semibold text-amber-900">
                          Active Session Detected
                        </h4>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-200/70 text-amber-800 uppercase tracking-wider">
                          {activeSession.role}
                        </span>
                      </div>
                      <p className="text-xs text-amber-800/90 mt-1 leading-relaxed">
                        You are currently signed in as <span className="font-semibold text-slate-900">{activeSession.name}</span>
                        {activeSession.email ? ` (${activeSession.email})` : ''}. Registering a new partner will start a fresh onboarding session.
                      </p>
                      <div className="flex items-center gap-2.5 mt-3 flex-wrap">
                        <button
                          type="button"
                          onClick={handleSignOutCurrentSession}
                          className="px-3.5 py-1.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold cursor-pointer transition-colors shadow-2xs flex items-center gap-1.5"
                        >
                          <LogOut className="w-3.5 h-3.5" />
                          <span>Sign out & Continue Registration</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate(-1)}
                          className="px-3.5 py-1.5 rounded-xl bg-white border border-amber-300 hover:bg-amber-100/60 text-amber-900 text-xs font-medium cursor-pointer transition-colors"
                        >
                          <span>Cancel / Return</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Partner Type Selection Cards (Hospital vs Ambulance Fleet) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {/* Hospital Selection Card */}
                <div
                  onClick={() => {
                    setPartnerType('hospital');
                    setErrors({});
                  }}
                  className={`p-3 rounded-xl border transition-all cursor-pointer flex items-center gap-3 select-none ${
                    partnerType === 'hospital'
                      ? 'border-blue-600 bg-blue-50/60 shadow-2xs'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      partnerType === 'hospital'
                        ? 'bg-blue-600 text-white shadow-2xs'
                        : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    <Building2 className="w-4.5 h-4.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs sm:text-sm font-semibold text-slate-800 leading-tight">
                      Hospital
                    </h3>
                    <p className="text-[11px] text-slate-500 mt-0.5 truncate font-normal">Healthcare facility</p>
                  </div>
                  {partnerType === 'hospital' && (
                    <div className="w-2 h-2 rounded-full bg-blue-600" />
                  )}
                </div>

                {/* Fleet Selection Card */}
                <div
                  onClick={() => {
                    setPartnerType('fleet');
                    setErrors({});
                  }}
                  className={`p-3 rounded-xl border transition-all cursor-pointer flex items-center gap-3 select-none ${
                    partnerType === 'fleet'
                      ? 'border-blue-600 bg-blue-50/60 shadow-2xs'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      partnerType === 'fleet'
                        ? 'bg-blue-600 text-white shadow-2xs'
                        : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    <Truck className="w-4.5 h-4.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs sm:text-sm font-semibold text-slate-800 leading-tight">
                      Ambulance Fleet
                    </h3>
                    <p className="text-[11px] text-slate-500 mt-0.5 truncate font-normal">Fleet owner / Operator</p>
                  </div>
                  {partnerType === 'fleet' && (
                    <div className="w-2 h-2 rounded-full bg-blue-600" />
                  )}
                </div>
              </div>

              {/* ========================================================
                  DYNAMIC FORM CONTENT BASED ON SELECTION
                  1. partnerType === 'hospital' -> ALL fields from Image 1
                  2. partnerType === 'fleet' -> ALL fields from Image 2
              ======================================================== */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {errors.submit && (
                  <div className="p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-2 animate-fade-in">
                    <AlertCircle className="w-4 h-4 shrink-0 text-red-600" />
                    <span>{errors.submit}</span>
                  </div>
                )}
                
                {/* ----------------- HOSPITAL REGISTRATION FIELDS (IMAGE 1) ----------------- */}
                {partnerType === 'hospital' && (
                  <>
                    {/* Section 1: Hospital Information */}
                    <div className="bg-slate-50/50 border border-slate-200/70 rounded-2xl p-4 sm:p-5 space-y-4">
                      <div className="flex items-center gap-2 pb-2.5 border-b border-slate-200/70">
                        <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 border border-blue-200/60 flex items-center justify-center shrink-0">
                          <Building2 className="w-4 h-4" />
                        </div>
                        <h3 className="text-xs sm:text-sm font-semibold text-slate-700 uppercase tracking-wider">
                          Hospital Information
                        </h3>
                      </div>

                      {/* Hospital Name & License Number (2 Columns) */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div data-error={!!errors.hospital_name}>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            Hospital Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="Enter hospital name"
                            value={hospitalForm.name}
                            onChange={(e) => handleHospitalChange('name', e.target.value)}
                            className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                              errors.hospital_name ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                            }`}
                          />
                          {errors.hospital_name && (
                            <p className="text-[11px] text-red-600 mt-1">{errors.hospital_name}</p>
                          )}
                        </div>

                        <div data-error={!!errors.licenseNumber}>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            License Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="Hospital license number"
                            value={hospitalForm.licenseNumber}
                            onChange={(e) => handleHospitalChange('licenseNumber', e.target.value)}
                            className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                              errors.licenseNumber ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                            }`}
                          />
                          {errors.licenseNumber && (
                            <p className="text-[11px] text-red-600 mt-1">{errors.licenseNumber}</p>
                          )}
                        </div>
                      </div>

                      {/* Address (Textarea) */}
                      <div data-error={!!errors.hospital_address}>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Address <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          rows={2}
                          placeholder="Enter complete address"
                          value={hospitalForm.address}
                          onChange={(e) => handleHospitalChange('address', e.target.value)}
                          className={`w-full px-3.5 py-2 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all resize-none ${
                            errors.hospital_address ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                          }`}
                        />
                        {errors.hospital_address && (
                          <p className="text-[11px] text-red-600 mt-1">{errors.hospital_address}</p>
                        )}
                      </div>

                      {/* City, State, Pincode (3 Columns) */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                        <div data-error={!!errors.hospital_city}>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            City <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="City"
                            value={hospitalForm.city}
                            onChange={(e) => handleHospitalChange('city', e.target.value)}
                            className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                              errors.hospital_city ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                            }`}
                          />
                          {errors.hospital_city && (
                            <p className="text-[11px] text-red-600 mt-1">{errors.hospital_city}</p>
                          )}
                        </div>

                        <div data-error={!!errors.hospital_state}>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            State <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="State"
                            value={hospitalForm.state}
                            onChange={(e) => handleHospitalChange('state', e.target.value)}
                            className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                              errors.hospital_state ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                            }`}
                          />
                          {errors.hospital_state && (
                            <p className="text-[11px] text-red-600 mt-1">{errors.hospital_state}</p>
                          )}
                        </div>

                        <div data-error={!!errors.hospital_pincode}>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            Pincode <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            maxLength={6}
                            placeholder="123456"
                            value={hospitalForm.pincode}
                            onChange={(e) => handleHospitalChange('pincode', e.target.value.replace(/\D/g, ''))}
                            className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                              errors.hospital_pincode ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                            }`}
                          />
                          {errors.hospital_pincode && (
                            <p className="text-[11px] text-red-600 mt-1">{errors.hospital_pincode}</p>
                          )}
                        </div>
                      </div>

                      {/* Fleet Operator Dropdown (From Image 1) */}
                      <div data-error={!!errors.operator_id}>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Fleet Operator <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Truck className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                          <select
                            value={hospitalForm.operator_id}
                            onChange={(e) => handleHospitalChange('operator_id', e.target.value)}
                            className="w-full pl-10 pr-9 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all appearance-none cursor-pointer"
                          >
                            <option value="">
                              {loadingOperators ? 'Loading fleet operators...' : 'Select Fleet Operator'}
                            </option>
                            {fleetOperators.map((operator) => (
                              <option key={operator.operator_id} value={operator.operator_id}>
                                {operator.name || operator.operator_id}{operator.city ? ` (${operator.city})` : ''}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        </div>
                        {errors.operator_id && (
                          <p className="text-[11px] text-red-600 mt-1">{errors.operator_id}</p>
                        )}
                      </div>
                    </div>

                    {/* Section 2: Admin User Information */}
                    <div className="bg-slate-50/50 border border-slate-200/70 rounded-2xl p-4 sm:p-5 space-y-4">
                      <div className="flex items-center gap-2 pb-2.5 border-b border-slate-200/70">
                        <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 border border-blue-200/60 flex items-center justify-center shrink-0">
                          <User className="w-4 h-4" />
                        </div>
                        <h3 className="text-xs sm:text-sm font-semibold text-slate-700 uppercase tracking-wider">
                          Admin User Information
                        </h3>
                      </div>

                      {/* Admin Name & Admin Email (2 Columns) */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div data-error={!!errors.admin_name}>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            Admin Name <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                            <input
                              type="text"
                              placeholder="Enter admin name"
                              value={hospitalForm.admin_name}
                              onChange={(e) => handleHospitalChange('admin_name', e.target.value)}
                              className={`w-full pl-10 pr-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                                errors.admin_name ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                              }`}
                            />
                          </div>
                          {errors.admin_name && (
                            <p className="text-[11px] text-red-600 mt-1">{errors.admin_name}</p>
                          )}
                        </div>

                        <div data-error={!!errors.admin_email}>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            Admin Email <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                            <input
                              type="email"
                              placeholder="Enter admin email"
                              value={hospitalForm.admin_email}
                              onChange={(e) => handleHospitalChange('admin_email', e.target.value)}
                              className={`w-full pl-10 pr-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                                errors.admin_email ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                              }`}
                            />
                          </div>
                          {errors.admin_email && (
                            <p className="text-[11px] text-red-600 mt-1">{errors.admin_email}</p>
                          )}
                        </div>
                      </div>

                      {/* Admin Phone (Full Width with Country Code 'IN v') */}
                      <div data-error={!!errors.admin_phone}>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Admin Phone <span className="text-red-500">*</span>
                        </label>
                        <div className="flex">
                          {/* Country Code Picker ('IN v') */}
                          <div className="relative w-20 shrink-0">
                            <select
                              value={hospitalForm.admin_phone_country_code}
                              onChange={(e) => handleHospitalChange('admin_phone_country_code', e.target.value)}
                              className="w-full h-10 px-2.5 bg-white border border-slate-200 rounded-l-xl text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-600 border-r-0 cursor-pointer appearance-none pl-3 pr-6"
                            >
                              <option value="+91">IN</option>
                              <option value="+1">US</option>
                              <option value="+44">UK</option>
                              <option value="+971">AE</option>
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                          </div>

                          <div className="flex-1 relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                            <input
                              type="tel"
                              maxLength={10}
                              placeholder="Enter admin phone number"
                              value={hospitalForm.admin_phone}
                              onChange={(e) => handleHospitalChange('admin_phone', e.target.value.replace(/\D/g, ''))}
                              className={`w-full pl-9 pr-3.5 h-10 bg-white border rounded-r-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                                errors.admin_phone ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                              }`}
                            />
                          </div>
                        </div>
                        {errors.admin_phone && (
                          <p className="text-[11px] text-red-600 mt-1">{errors.admin_phone}</p>
                        )}
                      </div>

                      {/* Temporary Password (With Eye Toggle & Generate Button) */}
                      <div data-error={!!errors.admin_temporary_password}>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Temporary Password <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                            <input
                              type={showHospitalPassword ? 'text' : 'password'}
                              placeholder="Set temporary password"
                              value={hospitalForm.admin_temporary_password}
                              onChange={(e) => handleHospitalChange('admin_temporary_password', e.target.value)}
                              className={`w-full pl-10 pr-10 h-10 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                                errors.admin_temporary_password ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                              }`}
                            />
                            <button
                              type="button"
                              onClick={() => setShowHospitalPassword(!showHospitalPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                            >
                              {showHospitalPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={handleGenerateHospitalPassword}
                            className="px-4 h-10 rounded-xl bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium text-xs transition-colors shrink-0 cursor-pointer shadow-2xs"
                          >
                            Generate
                          </button>
                        </div>
                        {errors.admin_temporary_password && (
                          <p className="text-[11px] text-red-600 mt-1">{errors.admin_temporary_password}</p>
                        )}

                        {/* Temporary Password Helper Note (Image 1) */}
                        <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50/70 border border-amber-200/60 px-3 py-2 rounded-xl mt-2 font-normal">
                          <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                          <span>This is a temporary password. The user will be required to change it on first login.</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* ----------------- FLEET OPERATOR REGISTRATION FIELDS (IMAGE 2) ----------------- */}
                {partnerType === 'fleet' && (
                  <>
                    {/* Section 1: Fleet Operator Information */}
                    <div className="bg-slate-50/50 border border-slate-200/70 rounded-2xl p-4 sm:p-5 space-y-4">
                      <div className="flex items-center gap-2 pb-2.5 border-b border-slate-200/70">
                        <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 border border-blue-200/60 flex items-center justify-center shrink-0">
                          <Truck className="w-4 h-4" />
                        </div>
                        <h3 className="text-xs sm:text-sm font-semibold text-slate-700 uppercase tracking-wider">
                          Fleet Operator Information
                        </h3>
                      </div>

                      {/* Fleet Operator Name & Contact Information (2 Columns) */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div data-error={!!errors.operator_name}>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            Fleet Operator Name <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <Building className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                            <input
                              type="text"
                              placeholder="Enter fleet operator name"
                              value={fleetForm.operator_name}
                              onChange={(e) => handleFleetChange('operator_name', e.target.value)}
                              className={`w-full pl-10 pr-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                                errors.operator_name ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                              }`}
                            />
                          </div>
                          {errors.operator_name && (
                            <p className="text-[11px] text-red-600 mt-1">{errors.operator_name}</p>
                          )}
                        </div>

                        <div data-error={!!errors.contact_info}>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            Contact Information <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="Contact information"
                            value={fleetForm.contact_info}
                            onChange={(e) => handleFleetChange('contact_info', e.target.value)}
                            className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                              errors.contact_info ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                            }`}
                          />
                          {errors.contact_info && (
                            <p className="text-[11px] text-red-600 mt-1">{errors.contact_info}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Section 2: Admin Information */}
                    <div className="bg-slate-50/50 border border-slate-200/70 rounded-2xl p-4 sm:p-5 space-y-4">
                      <div className="flex items-center gap-2 pb-2.5 border-b border-slate-200/70">
                        <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 border border-blue-200/60 flex items-center justify-center shrink-0">
                          <User className="w-4 h-4" />
                        </div>
                        <h3 className="text-xs sm:text-sm font-semibold text-slate-700 uppercase tracking-wider">
                          Admin Information
                        </h3>
                      </div>

                      {/* Admin Name & Admin Email (2 Columns) */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div data-error={!!errors.fleet_admin_name}>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            Admin Name <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                            <input
                              type="text"
                              placeholder="Enter admin name"
                              value={fleetForm.admin_name}
                              onChange={(e) => handleFleetChange('admin_name', e.target.value)}
                              className={`w-full pl-10 pr-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                                errors.fleet_admin_name ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                              }`}
                            />
                          </div>
                          {errors.fleet_admin_name && (
                            <p className="text-[11px] text-red-600 mt-1">{errors.fleet_admin_name}</p>
                          )}
                        </div>

                        <div data-error={!!errors.fleet_admin_email}>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            Admin Email <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                            <input
                              type="email"
                              placeholder="Enter admin email"
                              value={fleetForm.admin_email}
                              onChange={(e) => handleFleetChange('admin_email', e.target.value)}
                              className={`w-full pl-10 pr-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                                errors.fleet_admin_email ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                              }`}
                            />
                          </div>
                          {errors.fleet_admin_email && (
                            <p className="text-[11px] text-red-600 mt-1">{errors.fleet_admin_email}</p>
                          )}
                        </div>
                      </div>

                      {/* Admin Phone (Full Width with Country Code 'IN v') */}
                      <div data-error={!!errors.fleet_admin_phone}>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Admin Phone <span className="text-red-500">*</span>
                        </label>
                        <div className="flex">
                          {/* Country Code Picker ('IN v') */}
                          <div className="relative w-20 shrink-0">
                            <select
                              value={fleetForm.admin_phone_country_code}
                              onChange={(e) => handleFleetChange('admin_phone_country_code', e.target.value)}
                              className="w-full h-10 px-2.5 bg-white border border-slate-200 rounded-l-xl text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-600 border-r-0 cursor-pointer appearance-none pl-3 pr-6"
                            >
                              <option value="+91">IN</option>
                              <option value="+1">US</option>
                              <option value="+44">UK</option>
                              <option value="+971">AE</option>
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                          </div>

                          <div className="flex-1 relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                            <input
                              type="tel"
                              maxLength={10}
                              placeholder="Enter admin phone number"
                              value={fleetForm.admin_phone}
                              onChange={(e) => handleFleetChange('admin_phone', e.target.value.replace(/\D/g, ''))}
                              className={`w-full pl-9 pr-3.5 h-10 bg-white border rounded-r-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                                errors.fleet_admin_phone ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                              }`}
                            />
                          </div>
                        </div>
                        {errors.fleet_admin_phone && (
                          <p className="text-[11px] text-red-600 mt-1">{errors.fleet_admin_phone}</p>
                        )}
                      </div>

                      {/* Temporary Password */}
                      <div data-error={!!errors.fleet_admin_temporary_password}>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Temporary Password <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                            <input
                              type={showFleetPassword ? 'text' : 'password'}
                              placeholder="Set temporary password"
                              value={fleetForm.admin_temporary_password}
                              onChange={(e) => handleFleetChange('admin_temporary_password', e.target.value)}
                              className={`w-full pl-10 pr-10 h-10 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                                errors.fleet_admin_temporary_password ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                              }`}
                            />
                            <button
                              type="button"
                              onClick={() => setShowFleetPassword(!showFleetPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                            >
                              {showFleetPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={handleGenerateFleetPassword}
                            className="px-4 h-10 rounded-xl bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium text-xs transition-colors shrink-0 cursor-pointer shadow-2xs"
                          >
                            Generate
                          </button>
                        </div>
                        {errors.fleet_admin_temporary_password && (
                          <p className="text-[11px] text-red-600 mt-1">{errors.fleet_admin_temporary_password}</p>
                        )}

                        {/* Temporary Password Helper Note (Image 2) */}
                        <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50/70 border border-amber-200/60 px-3 py-2 rounded-xl mt-2 font-normal">
                          <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                          <span>This is a temporary password. The user will be required to change it on first login.</span>
                        </div>
                      </div>

                      {/* Confirm Temporary Password (Image 2) */}
                      <div data-error={!!errors.fleet_confirm_password}>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Confirm Temporary Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                          <input
                            type={showFleetConfirmPassword ? 'text' : 'password'}
                            placeholder="Confirm temporary password"
                            value={fleetForm.confirmPassword}
                            onChange={(e) => handleFleetChange('confirmPassword', e.target.value)}
                            className={`w-full pl-10 pr-10 h-10 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                              errors.fleet_confirm_password ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                            }`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowFleetConfirmPassword(!showFleetConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                          >
                            {showFleetConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {errors.fleet_confirm_password && (
                          <p className="text-[11px] text-red-600 mt-1">{errors.fleet_confirm_password}</p>
                        )}
                      </div>
                    </div>

                    {/* Section 3: Business Information (Optional) (Image 2) */}
                    <div className="bg-slate-50/50 border border-slate-200/70 rounded-2xl p-4 sm:p-5 space-y-4">
                      <div className="flex items-center gap-2 pb-2.5 border-b border-slate-200/70">
                        <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 border border-blue-200/60 flex items-center justify-center shrink-0">
                          <FileText className="w-4 h-4" />
                        </div>
                        <h3 className="text-xs sm:text-sm font-semibold text-slate-700 uppercase tracking-wider">
                          Business Information (Optional)
                        </h3>
                      </div>

                      {/* Business License & Emergency Contact (2 Columns) */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            Business License
                          </label>
                          <div className="relative">
                            <FileText className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                            <input
                              type="text"
                              placeholder="Business license number"
                              value={fleetForm.business_license}
                              onChange={(e) => handleFleetChange('business_license', e.target.value)}
                              className="w-full pl-10 pr-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            Emergency Contact
                          </label>
                          <div className="flex">
                            <div className="relative w-20 shrink-0">
                              <select
                                value={fleetForm.emergency_contact_country_code}
                                onChange={(e) => handleFleetChange('emergency_contact_country_code', e.target.value)}
                                className="w-full h-10 px-2.5 bg-white border border-slate-200 rounded-l-xl text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-600 border-r-0 cursor-pointer appearance-none pl-3 pr-6"
                              >
                                <option value="+91">IN</option>
                                <option value="+1">US</option>
                                <option value="+44">UK</option>
                                <option value="+971">AE</option>
                              </select>
                              <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>

                            <div className="flex-1 relative">
                              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                              <input
                                type="tel"
                                maxLength={10}
                                placeholder="Emergency contact number"
                                value={fleetForm.emergency_contact}
                                onChange={(e) => handleFleetChange('emergency_contact', e.target.value.replace(/\D/g, ''))}
                                className="w-full pl-9 pr-3.5 h-10 bg-white border border-slate-200 rounded-r-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Address (Textarea) */}
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Address
                        </label>
                        <div className="relative">
                          <MapPin className="absolute left-3.5 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
                          <textarea
                            rows={2}
                            placeholder="Enter complete address"
                            value={fleetForm.address}
                            onChange={(e) => handleFleetChange('address', e.target.value)}
                            className="w-full pl-10 pr-3.5 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all resize-none"
                          />
                        </div>
                      </div>

                      {/* City, State, Pincode (3 Columns) */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            City
                          </label>
                          <input
                            type="text"
                            placeholder="City"
                            value={fleetForm.city}
                            onChange={(e) => handleFleetChange('city', e.target.value)}
                            className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            State
                          </label>
                          <input
                            type="text"
                            placeholder="State"
                            value={fleetForm.state}
                            onChange={(e) => handleFleetChange('state', e.target.value)}
                            className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            Pincode
                          </label>
                          <input
                            type="text"
                            maxLength={6}
                            placeholder="123456"
                            value={fleetForm.pincode}
                            onChange={(e) => handleFleetChange('pincode', e.target.value.replace(/\D/g, ''))}
                            className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                          />
                        </div>
                      </div>

                      {/* Service Area */}
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Service Area
                        </label>
                        <input
                          type="text"
                          placeholder="Service area coverage"
                          value={fleetForm.service_area}
                          onChange={(e) => handleFleetChange('service_area', e.target.value)}
                          className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* NEXT Button (Solid Primary Blue) */}
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="w-full py-3 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs sm:text-sm shadow-xs hover:shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer mt-6 disabled:opacity-75 disabled:cursor-wait"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-white" />
                      <span>
                        {partnerType === 'hospital'
                          ? 'Creating hospital profile...'
                          : 'Registering fleet profile...'}
                      </span>
                    </>
                  ) : (
                    <>
                      <span>
                        {partnerType === 'hospital'
                          ? 'Next: Continue to Hospital Onboarding'
                          : 'Next: Continue to Fleet Onboarding'}
                      </span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Bottom Links */}
            <div className="pt-6 border-t border-slate-100 mt-6 flex flex-col items-center justify-center gap-2 text-xs text-slate-500">
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleVoiceIntakeClick}
                  className="text-slate-500 hover:text-blue-700 transition-colors cursor-pointer"
                >
                  Need assisted voice intake?{' '}
                  <span className="font-semibold text-blue-600 hover:text-blue-700 underline">Start voice</span>
                </button>
              </div>
              {/* <div className="text-center text-[11px] text-slate-400">
                All partner profiles are verified by our clinical integration team.
              </div> */}
            </div>
          </div>
        </div>
      </main>

      {/* Minimal Footer */}
      <footer className="py-5 text-center text-xs text-slate-400 border-t border-slate-200/80 bg-white">
        © {new Date().getFullYear()} RISA Smart Ambulance Platform. All rights reserved.
      </footer>
    </div>
  );
};

export default HospitalPartnerRegistrationPage;
